import React, { useState, useEffect, useMemo, memo, useCallback } from "react"
import Box from "@material-ui/core/Box"
import Typography from "@material-ui/core/Typography"
import { makeStyles } from "@material-ui/core/styles"
import Button from "@material-ui/core/Button"
import ViewDepartmentTable from "@/components/project-dashboard/view-department/ViewDepartmentTable"
import AddSingle from "@/components/project-dashboard/view-department/AddSingle"
import TextField from "@material-ui/core/TextField"
import Loading from "@/components/common/Loading"
import { getDepartments, updateDepartment, createNewDepartment, deleteDepartment } from "@/apis"
import EditDepartmentOrDesignation from "@/components/common/EditDepartmentOrDesignation"
import DeleteConfirmationDialog from "@/components/common/DeleteConfirmationDialog"
import { Breadcrumbs } from "@material-ui/core"
import { Link } from "react-router-dom"
import globalUseStyles from "@/globalUseStyles"
import PageHeader from "../shared-component/page-header"

const useStyles = makeStyles((theme) => ({
    wrapper: {
        padding: theme.spacing(4),
    },
    addNewActionButton: {
        marginRight: theme.spacing(2),
        marginBottom: theme.spacing(2),
    },
}))

const ViewDepartment = memo((props) => {
    const { companyId } = props
    const classes = useStyles()
    const globalClasses = globalUseStyles()

    const [isFormInvalid, setIsFormInvalid] = useState(false)
    const [openAddSingle, setOpenAddSingle] = useState(false)
    const [departmentToDelete, setDepartmentToDelete] = useState({})

    const handleAddNewDepartment = () => {
        setOpenAddSingle(true)
    }

    const handleAddSingleModalClose = () => {
        setOpenAddSingle(false)
    }
    const [departmentData, setDepartmentData] = useState({
        loading: true,
        error: false,
        data: {
            generalDepartment: [],
            companyUniqueDepartment: [],
        },
    })

    useEffect(() => {
        const getDepartmentsData = async () => {
            try {
                const departmentResult = await getDepartments(companyId)
                const companyUniqueDepartment = []
                const generalDepartment = []
                const departmentsDataArray = departmentResult.data?.departments ?? []
                departmentsDataArray.forEach((department) => {
                    if (department.companyId) companyUniqueDepartment.push(department)
                    else generalDepartment.push(department)
                })
                setDepartmentData({
                    loading: false,
                    error: false,
                    data: {
                        generalDepartment,
                        companyUniqueDepartment,
                    },
                })
            } catch (error) {
                setDepartmentData((prev) => ({
                    ...prev,
                    loading: false,
                    error: true,
                    data: prev.data,
                }))
            }
        }
        getDepartmentsData()
    }, [companyId])

    const [openEdit, setOpenEdit] = useState(false)
    const [openDelete, setOpenDelete] = useState(false)

    const handleEditClickOpen = () => {
        setOpenEdit(true)
    }

    const handleDeleteClickOpen = (open, department) => {
        setOpenDelete(open)
        setDepartmentToDelete(department)
    }

    const handleEditModalClose = () => {
        setOpenEdit(false)
    }

    const handleDeleteModalClose = () => {
        setOpenDelete(false)
    }

    const departmentFormDescriptions = useMemo(() => {
        return (
            <Typography gutterBottom>
                Enter a new name for the department you wish to be included in the assessment as a dropdown selection for participants
            </Typography>
        )
    }, [])

    const deleteModalDescription = useMemo(() => {
        return <Typography gutterBottom>Are you sure you want to delete department &quot;{departmentToDelete.department}&quot;?</Typography>
    }, [departmentToDelete])

    const [viewDepartmentEditDetail, setViewDepartmentEditDetail] = useState({
        display: false,
        departmentId: null,
        companyId: null,
        department: "",
    })

    const handleSetViewDepartmentEditDetail = (e) => {
        setViewDepartmentEditDetail(e)
    }

    const handleEditFormChange = (e) => {
        setIsFormInvalid(false)
        setViewDepartmentEditDetail((prev) => ({
            ...prev,
            department: e.target.value,
        }))
    }
    const formTextFields = useMemo(
        () => (
            <TextField
                error={isFormInvalid}
                autoFocus
                required
                label="Department Name"
                helperText={isFormInvalid && "department name required"}
                id="department-name"
                name="departmentName"
                placeholder="Enter Your Department Name"
                onChange={handleEditFormChange}
                defaultValue={viewDepartmentEditDetail.department}
                fullWidth
            />
        ),
        [viewDepartmentEditDetail]
    )

    const saveActions = useCallback(
        async (detail) => {
            const { departmentId, department } = detail
            const updateResult = await updateDepartment({
                departmentId,
                companyId,
                departmentName: department,
            })

            const { department: departmentUpdateName } = updateResult.data.departmentUpdated
            const newCompanyUniqueDepartment = departmentData.data.companyUniqueDepartment.map((departmentItem) => {
                return departmentItem.departmentId === departmentId
                    ? {
                          ...departmentItem,
                          department: departmentUpdateName,
                      }
                    : departmentItem
            })
            setDepartmentData((prev) => ({
                ...prev,
                data: {
                    generalDepartment: [...prev.data.generalDepartment],
                    companyUniqueDepartment: newCompanyUniqueDepartment,
                },
            }))
        },
        [departmentData.data]
    )

    const deleteAction = useCallback(async () => {
        const { companyId: companyIdDelete, departmentId } = departmentToDelete
        const result = await deleteDepartment(departmentId, companyIdDelete);

        const newDepartmentList = departmentData.data.companyUniqueDepartment.filter(
            (department) => {
                return department.departmentId != result.data.departmentId
            }
        );

        setDepartmentData((prev) => {
            return {
                ...prev,
                data: {
                    generalDepartment: [...prev.data.generalDepartment],
                    companyUniqueDepartment: newDepartmentList
                },
            }
        })
    }, [departmentToDelete])

    const addSingleActions = useCallback(
        async (newSingleName) => {
            const newCreateResult = await createNewDepartment({
                companyId,
                departments: newSingleName,
            })

            const newDepartments = newCreateResult.data.departments.map((newDepartment) => {
                const { companyId: companyIdNew, department, departmentId, departmentVerId, industryId, roleId } = newDepartment

                return { companyId: companyIdNew, department, departmentId, departmentVerId, industryId, roleId }
            })

            const newCompanyUniqueDepartment = [...newDepartments, ...departmentData.data.companyUniqueDepartment]

            setDepartmentData((prev) => ({
                ...prev,
                data: {
                    generalDepartment: [...prev.data.generalDepartment],
                    companyUniqueDepartment: newCompanyUniqueDepartment,
                },
            }))
        },
        [departmentData, companyId]
    )

    const Links = () => (
        <Breadcrumbs aria-label="breadcrumb">
            <Link color="inherit" to="/" className={globalClasses.link}>
                Home
            </Link>
            <Typography color="textPrimary">Departments</Typography>
        </Breadcrumbs>
    )

    return (
        <>
            <PageHeader pageTitle="Departments" links={<Links />} />
            {departmentData.loading ? (
                <div className="wrapper">
                    <Loading />
                </div>
            ) : (
                <>
                    {departmentData.error ? (
                        <div>Error</div>
                    ) : (
                        <Box className={classes.wrapper}>
                            <Box>
                                <Box className="add-new-department-button">
                                    <Button
                                        variant="contained"
                                        color="primary"
                                        className={classes.addNewActionButton}
                                        onClick={() => handleAddNewDepartment()}
                                    >
                                        Add Department
                                    </Button>
                                </Box>
                            </Box>
                            <Box mt={3}>
                                <ViewDepartmentTable
                                    departmentData={departmentData}
                                    handleEditClickOpen={handleEditClickOpen}
                                    handleDeleteClickOpen={handleDeleteClickOpen}
                                    handleSetViewDepartmentEditDetail={handleSetViewDepartmentEditDetail}
                                />
                            </Box>
                        </Box>
                    )}
                </>
            )}

            <EditDepartmentOrDesignation
                handleModalClose={handleEditModalClose}
                open={openEdit}
                section="Department"
                descriptions={departmentFormDescriptions}
                formTextFields={formTextFields}
                editDetail={viewDepartmentEditDetail}
                saveActions={saveActions}
            />

            <DeleteConfirmationDialog
                handleModalClose={handleDeleteModalClose}
                open={openDelete}
                section="Department"
                description={deleteModalDescription}
                deleteAction={deleteAction}
            />

            <AddSingle handleModalClose={handleAddSingleModalClose} open={openAddSingle} addActions={addSingleActions} />
        </>
    )
})

export default ViewDepartment
